import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { toUniqueUuidList } from "@/utils/uuid";
import { uploadAdminFiles, uploadClientFiles } from "@/lib/files.api";
import type {
  AddVirtualClassroomParticipantsDto,
  BindVirtualClassroomFilesDto,
  UpdateVirtualClassroomDto,
  VirtualClassroomFileResDto,
  VirtualClassroomResDto,
} from "../types/virtual-classroom.types";

export type {
  AddVirtualClassroomParticipantsDto,
  BindVirtualClassroomFilesDto,
  CreateVirtualClassroomDto,
  UpdateVirtualClassroomDto,
  VirtualClassroomFileResDto,
  VirtualClassroomResDto,
  VirtualClassroomScheduledTimeDto,
} from "../types/virtual-classroom.types";

const VIRTUAL_CLASSROOMS_ENDPOINT = "/api/virtual-classrooms";
const CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT = "/api/client/virtual-classrooms";
const EDUCATIONS_ENDPOINT = "/api/educations";

const REMOVED_USER_IDS_MAX = 20;
const ADD_PARTICIPANTS_USER_IDS_MAX = 20;
const BIND_FILES_MAX = 20;

/**
 * Admin — Sanal sınıf güncelleme
 * PATCH /api/virtual-classrooms/:id
 * Yetki: EDUCATIONS.UPDATE (JWT)
 *
 * Boş gövde → backend 400 VIRTUAL_CLASSROOM_NOTHING_TO_UPDATE
 * Geçersiz removedUserIds → 400 VIRTUAL_CLASSROOM_REMOVED_USER_NOT_PARTICIPANT
 */
export const virtualClassroomsApi = {
  /**
   * GET /api/educations/:educationId/virtual-classrooms
   * Swagger: VirtualClassroomResDtoPaginatedResponse — data: VirtualClassroomResDto[]
   */
  listByEducation: async (
    educationId: string,
    params?: { offeringId?: string },
  ): Promise<VirtualClassroomResDto[]> => {
    const { data: response } = await apiClient.get<
      | VirtualClassroomResDto[]
      | { data?: VirtualClassroomResDto[] }
    >(`${EDUCATIONS_ENDPOINT}/${educationId}/virtual-classrooms`, {
      params,
    });
    if (Array.isArray(response)) return response;
    const wrapped = response as { data?: VirtualClassroomResDto[] };
    return wrapped?.data ?? [];
  },

  /**
   * POST /api/virtual-classrooms/:id/participants
   * Body: { userIds } — 1..20 UUID (Swagger AddVirtualClassroomParticipantsDto)
   */
  addParticipants: async (
    id: string,
    userIds: string[],
  ): Promise<void> => {
    // Katılımcı listeleri planning-groups cevabından türetiliyor; kullanıcısı
    // çözülemeyen satırlar geçmişte boş string olarak sızıp isteğin tamamını
    // "each value in userIds must be a UUID" ile düşürüyordu. Gönderim öncesi ayıkla.
    const cleanUserIds = toUniqueUuidList(userIds);
    if (cleanUserIds.length === 0) {
      throw new Error(i18n.t("virtualClassroomsApi.participantsNoValidUser"));
    }
    if (cleanUserIds.length > ADD_PARTICIPANTS_USER_IDS_MAX) {
      throw new Error(
        `Katılımcı eklerken userIds 1–${ADD_PARTICIPANTS_USER_IDS_MAX} arasında olmalıdır.`,
      );
    }
    const body: AddVirtualClassroomParticipantsDto = { userIds: cleanUserIds };
    await apiClient.post(
      `${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/participants`,
      body,
    );
  },

  /**
   * DELETE /api/virtual-classrooms/:id — sınıfı soft delete eder,
   * katılımcıların enrollment'ı ENROLLED ise APPROVED_BRANCH_MANAGER'a geri alınır.
   */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}`);
  },

  /**
   * POST /api/virtual-classrooms/:id/complete — sınıfı tamamla,
   * katılımcılara push bildirim gönderir. Opsiyonel `body.decisions` verilirse
   * yalnız bu sınıfın katılımcılarının enrollment'ları state machine üzerinden
   * ilerletilir; eğitim+offering durumu DEĞİŞMEZ.
   */
  complete: async (
    id: string,
    body?: {
      decisions?: Array<{
        enrollmentId: string;
        decision: "COMPLETED" | "NOT_COMPLETED" | "BLACKLISTED";
        excuseReason?: number;
      }>;
      blacklist?: {
        reason: number;
        startDate: string;
        endDate: string;
        description?: string;
      };
    },
  ): Promise<VirtualClassroomResDto> => {
    const { data } = await apiClient.post<
      VirtualClassroomResDto | { data?: VirtualClassroomResDto }
    >(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/complete`, body ?? {});
    const entity =
      (data as { data?: VirtualClassroomResDto })?.data ??
      (data as VirtualClassroomResDto);
    if (!entity?.id) {
      throw new Error(i18n.t("virtualClassroomsApi.completeInvalid"));
    }
    return entity;
  },

  update: async (
    id: string,
    body: UpdateVirtualClassroomDto,
  ): Promise<VirtualClassroomResDto> => {
    // `removedUserIds` / `instructorIds` de backend'de `@IsUUID('4', { each: true })`
    // ile korunuyor; geçersiz değer tüm PATCH'i düşürmesin diye burada temizleniyor.
    // Alan hiç gönderilmediyse dokunulmuyor (undefined = "değiştirme").
    const cleanBody: UpdateVirtualClassroomDto = {
      ...body,
      ...(body.removedUserIds !== undefined
        ? { removedUserIds: toUniqueUuidList(body.removedUserIds) }
        : {}),
      ...(body.instructorIds !== undefined
        ? { instructorIds: toUniqueUuidList(body.instructorIds) }
        : {}),
    };

    if (
      cleanBody.removedUserIds &&
      cleanBody.removedUserIds.length > REMOVED_USER_IDS_MAX
    ) {
      throw new Error(
        `removedUserIds en fazla ${REMOVED_USER_IDS_MAX} olabilir.`,
      );
    }

    const { data } = await apiClient.patch<
      VirtualClassroomResDto | { data?: VirtualClassroomResDto }
    >(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}`, cleanBody);

    const entity =
      (data as { data?: VirtualClassroomResDto })?.data ??
      (data as VirtualClassroomResDto);
    if (!entity?.id) {
      throw new Error(i18n.t("virtualClassroomsApi.updateInvalid"));
    }
    return entity;
  },

  /**
   * GET /api/virtual-classrooms/:id/files
   * Admin/officer/manager — sınıfa atanmış dosyaları listeler.
   */
  listFiles: async (id: string): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/client/virtual-classrooms/:id/files
   * Kursiyer — sınıfa katılımcıysa dosyaları listeler.
   */
  listFilesForClient: async (
    id: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/client/educations/:educationId/virtual-classroom-files
   * Bir eğitimin tüm sanal sınıflarına bağlı dosyaları (kursiyer için) tek
   * çağrıda döner. Her sınıf için ayrı listFilesForClient çağrısının yerini
   * alır.
   */
  listFilesForClientByEducation: async (
    educationId: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    const { data } = await apiClient.get<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`/api/client/educations/${educationId}/virtual-classroom-files`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * Önce /api/admin/files/upload ile yükleyip sonra
   * POST /api/virtual-classrooms/:id/files ile sınıfa bind eder.
   * Tek atımlı helper — modal'larda upload-and-bind tek aksiyon.
   */
  uploadAndBindFiles: async (
    classroomId: string,
    files: File[],
    displayName?: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    if (files.length === 0) return [];
    if (files.length > BIND_FILES_MAX) {
      throw new Error(
        `Bir seferde en fazla ${BIND_FILES_MAX} dosya yüklenebilir.`,
      );
    }
    const uploaded = await uploadAdminFiles(files);
    const fileIds = uploaded.map((f) => f.id);
    if (fileIds.length === 0) return [];
    const body: BindVirtualClassroomFilesDto = {
      fileIds,
      ...(displayName ? { displayName } : {}),
    };
    const { data } = await apiClient.post<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${classroomId}/files`, body);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/client/virtual-classrooms/:id/jitsi-config
   * Kursiyer katılım kapısı: saat penceresi dışında ya da eğitmen dersi
   * başlatmadıysa backend 400 döner — katılım öncesi bu uçla doğrulanır.
   */
  getClientJitsiConfig: async (
    id: string,
  ): Promise<{ roomName: string; fullUrl: string }> => {
    const { data } = await apiClient.get<{
      data?: { roomName: string; fullUrl: string };
    }>(`${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/jitsi-config`);
    return data?.data ?? ({ roomName: "", fullUrl: "" } as never);
  },

  /**
   * POST /api/client/virtual-classrooms/:id/start
   * Eğitmenin "dersi başlat" aksiyonu — kursiyer katılım kapısını açar.
   */
  startLiveSession: async (id: string): Promise<void> => {
    await apiClient.post(`${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/start`);
  },

  /**
   * POST /api/client/virtual-classrooms/:id/presence
   * Canlı sınıfa giriş kaydı (yoklamada içeride kalınan süre). Hatalar sessiz:
   * kayıt açılamasa da ders sürer.
   */
  joinPresence: async (
    id: string,
  ): Promise<{ presenceId: string; heartbeatIntervalSeconds: number }> => {
    type JoinRes = { presenceId: string; heartbeatIntervalSeconds: number };
    const { data } = await apiClient.post<{ data?: JoinRes } | JoinRes>(
      `${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/presence`,
      undefined,
      { skipErrorToast: true },
    );
    return ((data as { data?: JoinRes })?.data ?? data) as JoinRes;
  },

  /** POST /api/client/virtual-classrooms/:id/presence/:presenceId/heartbeat — kayıt kapanmışsa 404 */
  heartbeatPresence: async (id: string, presenceId: string): Promise<void> => {
    await apiClient.post(
      `${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/presence/${presenceId}/heartbeat`,
      undefined,
      { skipErrorToast: true },
    );
  },

  /** POST /api/client/virtual-classrooms/:id/presence/:presenceId/leave */
  leavePresence: async (id: string, presenceId: string): Promise<void> => {
    await apiClient.post(
      virtualClassroomsApi.presenceLeavePath(id, presenceId),
      undefined,
      { skipErrorToast: true },
    );
  },

  /** Sayfa kapanırken `fetch(..., { keepalive })` ile gönderilecek çıkış yolu. */
  presenceLeavePath: (id: string, presenceId: string): string =>
    `${CLIENT_VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/presence/${presenceId}/leave`,

  /**
   * GET /api/virtual-classrooms/:id/supervisor-recipients
   * Sınıf katılımcılarının departmanlarındaki Eğitim Sorumluları — sınıf
   * mailine opsiyonel alıcı olarak eklenir.
   */
  listSupervisorRecipients: async (
    id: string,
  ): Promise<Array<{ id: string; firstName: string; lastName: string }>> => {
    // Backend `{ items, count }` döner; TransformResponseInterceptor bunu
    // `{ data: items, count }` biçimine çevirir. Eskiden `data.data.items`
    // okunuyordu — o alan hiç olmadığı için sorumlular backend'den gelse bile
    // liste boş sayılıyor, "Eğitim Sorumlusu bulunamadı" uyarısı çıkıyordu.
    const { data } = await apiClient.get<{
      items?: Array<{ id: string; firstName: string; lastName: string }>;
      data?: Array<{ id: string; firstName: string; lastName: string }>;
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/supervisor-recipients`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * GET /api/virtual-classrooms/:id/manager-recipients
   * Sınıf katılımcılarının Daire Başkanı / İşletme Müdürü — sınıf mailine
   * alıcı olarak eklenir.
   */
  listManagerRecipients: async (
    id: string,
  ): Promise<Array<{ id: string; firstName: string; lastName: string }>> => {
    const { data } = await apiClient.get<{
      items?: Array<{ id: string; firstName: string; lastName: string }>;
      data?: Array<{ id: string; firstName: string; lastName: string }>;
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/manager-recipients`);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * POST /api/virtual-classrooms/:id/participants/pdf
   * Sınıftaki kursiyer listesini PDF olarak üretir; dönen dosya sınıf mailine
   * ek olarak konur.
   */
  createParticipantListPdf: async (
    id: string,
  ): Promise<{ id: string; originalName: string; size: number; downloadUrl: string | null }> => {
    type PdfFile = { id?: string; originalName?: string; size?: number; downloadUrl?: string | null };
    const { data } = await apiClient.post<PdfFile & { data?: PdfFile }>(
      `${VIRTUAL_CLASSROOMS_ENDPOINT}/${id}/participants/pdf`,
    );
    const file = data?.data ?? data;
    return {
      id: String(file?.id ?? ""),
      originalName: String(file?.originalName ?? ""),
      size: Number(file?.size ?? 0),
      downloadUrl: file?.downloadUrl ?? null,
    };
  },

  /**
   * Eğitmen için upload-and-bind: kaynak dosyayı client upload ucuyla yükler
   * (admin FILES yetkisi gerektirmez), ardından aynı bind ucuna gönderir —
   * bind ucu atanmış eğitmene açık (EducationAccess allowInstructor).
   */
  uploadAndBindFilesAsInstructor: async (
    classroomId: string,
    files: File[],
    displayName?: string,
  ): Promise<VirtualClassroomFileResDto[]> => {
    if (files.length === 0) return [];
    if (files.length > BIND_FILES_MAX) {
      throw new Error(
        `Bir seferde en fazla ${BIND_FILES_MAX} dosya yüklenebilir.`,
      );
    }
    const uploaded = await uploadClientFiles(files);
    const fileIds = uploaded.map((f) => f.id);
    if (fileIds.length === 0) return [];
    const body: BindVirtualClassroomFilesDto = {
      fileIds,
      ...(displayName ? { displayName } : {}),
    };
    const { data } = await apiClient.post<{
      items?: VirtualClassroomFileResDto[];
      data?: VirtualClassroomFileResDto[];
    }>(`${VIRTUAL_CLASSROOMS_ENDPOINT}/${classroomId}/files`, body);
    const items = data?.items ?? data?.data;
    return Array.isArray(items) ? items : [];
  },

  /**
   * DELETE /api/virtual-classrooms/:id/files/:fileId
   * Sadece join row'unu kaldırır; files tablosundaki kaynak dosya korunur.
   */
  unbindFile: async (classroomId: string, fileId: string): Promise<void> => {
    await apiClient.delete(
      `${VIRTUAL_CLASSROOMS_ENDPOINT}/${classroomId}/files/${fileId}`,
    );
  },
};
